import { useState } from 'react';
import { format } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Edit3, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

interface DateTimeInlineEditorProps {
  currentDueDate: Date;
  onSave: (newDueDate: Date) => Promise<void>;
  isManuallyAdjusted?: boolean;
}

export function DateTimeInlineEditor({ currentDueDate, onSave, isManuallyAdjusted }: DateTimeInlineEditorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(currentDueDate);
  const [selectedTime, setSelectedTime] = useState(format(currentDueDate, 'HH:mm'));
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const handleSave = async () => {
    if (!selectedDate) return;

    // Combine date and time
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const newDueDate = new Date(selectedDate);
    newDueDate.setHours(hours, minutes, 0, 0);

    // Validate: don't allow backdating more than 15 minutes
    const now = new Date();
    const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);
    
    if (newDueDate < fifteenMinutesAgo) {
      toast({
        title: "Invalid Date",
        description: "Due date cannot be more than 15 minutes in the past",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      await onSave(newDueDate);
      setIsOpen(false);
      toast({
        title: "Success",
        description: "Due date updated successfully",
      });
    } catch (error) {
      console.error('[DateTimeInlineEditor] onSave failed:', error);
      toast({
        title: "Error",
        description: "Failed to update due date",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedDate(currentDueDate);
    setSelectedTime(format(currentDueDate, 'HH:mm'));
    setIsOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <Edit3 className="h-3 w-3 mr-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4" align="start">
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Due Date</label>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => {
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  return date < yesterday;
                }}
                className={cn("p-3 pointer-events-auto")}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Time</label>
              <Input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving || !selectedDate}
                className="flex-1"
              >
                <Check className="h-3 w-3 mr-1" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancel}
                className="flex-1"
              >
                <X className="h-3 w-3 mr-1" />
                Cancel
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      
      {isManuallyAdjusted && (
        <Badge variant="outline" className="text-xs">
          Adjusted
        </Badge>
      )}
    </div>
  );
}